import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Home, 
  Users, 
  CreditCard, 
  Calendar, 
  BarChart3, 
  Settings as SettingsIcon, 
  LogOut, 
  Menu,
  X,
  Bell,
  User,
  Shield,
  Tag,
  DollarSign,
  Wrench
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Dashboard from '../pages/Dashboard';
import Members from '../pages/Members';
import Debts from '../pages/Debts';
import Services from '../pages/Services';
import Events from '../pages/Events';
import Campaigns from '../pages/Campaigns';
import CampaignDetail from '../pages/CampaignDetail';
import Reports from '../pages/Reports';
import Notifications from '../pages/Notifications';
import Settings from '../pages/Settings';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
  overflow-x: hidden;
  width: 100%;
  max-width: 100vw;
`;

const Sidebar = styled.div<{ $isOpen: boolean }>`
  width: 280px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 1000;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  transition: transform 0.3s ease;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  
  @media (min-width: 768px) {
    position: static;
    transform: none;
    display: block;
    margin-right: auto; /* dock to the left in flex row */
  }
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const CloseSidebarButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  @media (min-width: 768px) {
    display: none; /* Hide on desktop */
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SidebarContent = styled.div`
  padding: 20px 0;
`;

const NavItem = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-right: 3px solid ${props => props.$active ? 'white' : 'transparent'};
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const NavText = styled.span`
  font-size: 16px;
  font-weight: 500;
`;

const UserSection = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 16px;
`;

const LogoutButton = styled.button`
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 0;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
  
  @media (min-width: 768px) {
    margin-left: 280px; /* leave space for left-docked sidebar */
    max-width: calc(100vw - 280px);
    padding-right: 0; /* Remove any right padding */
  }
`;

const Header = styled.div`
  background: white;
  padding: 15px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  position: relative;
  
  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  padding: 20px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  margin-right: 0; /* Remove any right margin for RTL */
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const Layout: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Prevent horizontal scroll
  React.useEffect(() => {
    document.body.style.overflowX = 'hidden';
    document.documentElement.style.overflowX = 'hidden';
    
    return () => {
      document.body.style.overflowX = 'unset';
      document.documentElement.style.overflowX = 'unset';
    };
  }, []);

  const navigationItems = [
    { path: '/', icon: Home, label: 'דף ראשי' },
    { path: '/members', icon: Users, label: 'מתפללים', adminOnly: true },
    { path: '/debts', icon: CreditCard, label: 'חייבים', adminOnly: true },
    { path: '/services', icon: Wrench, label: 'שירותים', adminOnly: true },
    { path: '/events', icon: Calendar, label: 'אירועים' },
    { path: '/campaigns', icon: DollarSign, label: 'קמפיינים' },
    { path: '/reports', icon: BarChart3, label: 'דוחות', adminOnly: true },
    { path: '/notifications', icon: Bell, label: 'הודעות', adminOnly: true },
    { path: '/settings', icon: SettingsIcon, label: 'הגדרות', adminOnly: true },
  ];

  const filteredNavItems = navigationItems.filter(item => 
    !item.adminOnly || isAdmin
  );

  console.log('🔍 Layout - User:', user);
  console.log('🔍 Layout - isAdmin:', isAdmin);
  console.log('🔍 Layout - filteredNavItems:', filteredNavItems.map(item => item.path));

  const handleNavigation = (path: string) => {
    console.log('🚀 Navigating to:', path);
    console.log('🔍 Current pathname:', location.pathname);
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPageTitle = () => {
    const currentItem = navigationItems.find(item => item.path === location.pathname);
    return currentItem?.label || 'זכרון דליה';
  };

  return (
    <LayoutContainer>
      <Overlay $isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo>
            <Shield size={24} />
            זכרון דליה
          </Logo>
          <CloseSidebarButton onClick={() => setSidebarOpen(false)}>
            ×
          </CloseSidebarButton>
        </SidebarHeader>
        
        <SidebarContent>
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavItem
                key={item.path}
                $active={isActive}
                onClick={() => handleNavigation(item.path)}
              >
                <Icon size={20} />
                <NavText>{item.label}</NavText>
              </NavItem>
            );
          })}
        </SidebarContent>
        
        <UserSection>
          <UserInfo>
            <User size={20} />
            <UserName>{user?.name} {user?.lastName}</UserName>
          </UserInfo>
          <LogoutButton onClick={handleLogout}>
            <LogOut size={16} />
            התנתק
          </LogoutButton>
        </UserSection>
      </Sidebar>

      <MainContent>
        <Header>
          <MenuButton onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </MenuButton>
          <HeaderTitle>{getPageTitle()}</HeaderTitle>
          <NotificationButton>
            <Bell size={20} />
          </NotificationButton>
        </Header>
        
        <Content>
          {(() => {
            console.log('🎯 Rendering content for path:', location.pathname);
            if (location.pathname === '/debt-categories') {
              console.log('✅ Rendering DebtCategories component');
            }
            return null;
          })()}
          {location.pathname === '/' && <Dashboard />}
          {location.pathname === '/members' && <Members />}
          {location.pathname === '/debts' && <Debts />}
          {location.pathname === '/services' && <Services />}
          {location.pathname === '/events' && <Events />}
          {location.pathname === '/campaigns' && <Campaigns />}
          {location.pathname.startsWith('/campaigns/') && <CampaignDetail />}
          {location.pathname === '/reports' && <Reports />}
          {location.pathname === '/notifications' && <Notifications />}
          {location.pathname === '/settings' && <Settings />}
        </Content>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
